import { motion } from 'framer-motion';

// ─── FloatingPaths ────────────────────────────────────────────────────────────
// Renders animated SVG curves. Pass position={1} and position={-1} to get
// two mirrored sets (as in the original BackgroundPaths component).
// Adapted for a dark/blue background: strokes are white at low opacity.

export function FloatingPaths({ position = 1 }) {
    const paths = Array.from({ length: 36 }, (_, i) => ({
        id: i,
        d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
            380 - i * 5 * position
        } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
            152 - i * 5 * position
        } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
            684 - i * 5 * position
        } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
        // Subtle white strokes – opacity grows with index
        opacity: 0.025 + i * 0.007,
        width:   0.5   + i * 0.025,
        // Deterministic duration per path (avoids Math.random re-render issues)
        duration: 20 + (i * 7 % 10),
    }));

    return (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
            <svg
                style={{ width: '100%', height: '100%' }}
                viewBox="0 0 696 316"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
            >
                <title>Background Paths</title>
                {paths.map((path) => (
                    <motion.path
                        key={path.id}
                        d={path.d}
                        stroke="rgba(255,255,255,1)"
                        strokeWidth={path.width}
                        strokeOpacity={path.opacity}
                        initial={{ pathLength: 0.3, opacity: 0.6 }}
                        animate={{
                            pathLength:  1,
                            opacity:     [0.3, 0.6, 0.3],
                            pathOffset:  [0, 1, 0],
                        }}
                        transition={{
                            duration: path.duration,
                            repeat:   Infinity,
                            ease:     'linear',
                        }}
                    />
                ))}
            </svg>
        </div>
    );
}

// ─── AnimatedWord ─────────────────────────────────────────────────────────────
// Animates each letter of a word with a spring entrance.
// Props:
//   word     – string to animate
//   delay    – base delay before the first letter starts (default 0)
//   className – optional className for the outer span wrapper
//   style     – optional style for the outer span wrapper

export function AnimatedWord({ word = '', delay = 0, className = '', style = {} }) {
    return (
        <span className={className} style={{ display: 'inline-block', ...style }}>
            {word.split('').map((letter, i) => (
                <motion.span
                    key={i}
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0,  opacity: 1 }}
                    transition={{
                        delay:     delay + i * 0.04,
                        type:      'spring',
                        stiffness: 160,
                        damping:   22,
                    }}
                    style={{ display: 'inline-block' }}
                >
                    {letter}
                </motion.span>
            ))}
        </span>
    );
}

// ─── BackgroundPaths (full standalone component) ──────────────────────────────
// Drop-in version matching the original component API.
// title prop splits on spaces and animates each word's letters.
// The button text can be customised via buttonLabel / onButtonClick.

export function BackgroundPaths({
    title       = 'Background Paths',
    buttonLabel = 'Discover Excellence',
    onButtonClick,
}) {
    const words = title.split(' ');

    return (
        <div style={{
            position:       'relative',
            minHeight:      '100vh',
            width:          '100%',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            overflow:       'hidden',
            background:     'linear-gradient(150deg, #1a6fb5 0%, #1050a0 45%, #0a3578 100%)',
        }}>
            <FloatingPaths position={1}  />
            <FloatingPaths position={-1} />

            <div style={{
                position:  'relative',
                zIndex:    10,
                textAlign: 'center',
                padding:   '0 1.5rem',
                maxWidth:  '900px',
                margin:    '0 auto',
            }}>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5 }}
                >
                    <h1 style={{
                        fontSize:      'clamp(3rem, 8vw, 6rem)',
                        fontWeight:    900,
                        marginBottom:  '2.5rem',
                        letterSpacing: '-0.03em',
                        lineHeight:    1.05,
                    }}>
                        {words.map((word, wi) => (
                            <span key={wi} style={{ display: 'inline-block', marginRight: '0.25em' }}>
                                <AnimatedWord word={word} delay={wi * 0.1} style={{ color: 'white' }} />
                            </span>
                        ))}
                    </h1>

                    <div style={{
                        display:  'inline-block',
                        padding:  '1px',
                        borderRadius: '18px',
                        background: 'linear-gradient(to bottom, rgba(255,255,255,0.2), rgba(255,255,255,0.05))',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    }}>
                        <button
                            onClick={onButtonClick}
                            style={{
                                display:        'inline-flex',
                                alignItems:     'center',
                                gap:            '0.75rem',
                                padding:        '0.875rem 2.25rem',
                                borderRadius:   '16px',
                                border:         '1px solid rgba(255,255,255,0.15)',
                                background:     'rgba(255,255,255,0.12)',
                                backdropFilter: 'blur(8px)',
                                color:          'white',
                                fontSize:       '1.0625rem',
                                fontWeight:     600,
                                cursor:         'pointer',
                                transition:     'all 0.25s ease',
                                letterSpacing:  '0.01em',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                                e.currentTarget.style.transform  = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                                e.currentTarget.style.transform  = 'translateY(0)';
                            }}
                        >
                            <span style={{ opacity: 0.9 }}>{buttonLabel}</span>
                            <span style={{ fontSize: '1.25rem', opacity: 0.75, transition: 'transform 0.25s ease' }}>→</span>
                        </button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
